import { useState, useCallback } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api.js';
import { invalidateIssueViews } from '@/hooks/useIssues.js';
import { usePriorities, useAssignableUsers } from '@/hooks/useMeta.js';
import { AssigneeOptions } from '@/components/AssigneeOptions.js';

interface EditableFieldProps {
  issueKey: string;
  field: string;
  value: string;
  projectKey: string;
}

export function EditableSummary({ issueKey, value }: { issueKey: string; value: string }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: (summary: string) => api.put(`/api/issues/${issueKey}`, { summary }),
    onSuccess: () => {
      invalidateIssueViews(queryClient, issueKey);
      setEditing(false);
    },
  });

  const handleSave = useCallback(() => {
    if (draft.trim() && draft.trim() !== value) {
      mutation.mutate(draft.trim());
    } else {
      setEditing(false);
    }
  }, [draft, value, mutation]);

  if (!editing) {
    return (
      <h1
        className="-mx-1.5 cursor-pointer rounded px-1.5 py-0.5 text-2xl font-semibold leading-snug text-gray-900 hover:bg-gray-100"
        onClick={() => { setDraft(value); setEditing(true); }}
        title="Click to edit"
      >
        {value}
      </h1>
    );
  }

  return (
    <div className="flex gap-2">
      <input
        type="text"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter') handleSave(); if (e.key === 'Escape') setEditing(false); }}
        autoFocus
        maxLength={255}
        className="flex-1 rounded-md border px-2 py-1 text-lg font-bold"
      />
      <button onClick={handleSave} disabled={mutation.isPending} className="rounded-md bg-blue-600 px-3 py-1 text-sm text-white hover:bg-blue-700 disabled:opacity-50">
        Save
      </button>
      <button onClick={() => { setDraft(value); setEditing(false); }} className="rounded-md border px-3 py-1 text-sm text-gray-600 hover:bg-gray-50">
        Cancel
      </button>
    </div>
  );
}

export function EditablePriority({ issueKey, value, projectKey: _projectKey }: EditableFieldProps) {
  const [editing, setEditing] = useState(false);
  const { data: priorities } = usePriorities();
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: (priority: string) => api.put(`/api/issues/${issueKey}`, { priority }),
    onSuccess: () => {
      invalidateIssueViews(queryClient, issueKey);
      setEditing(false);
    },
  });

  if (!editing) {
    return (
      <span
        className="cursor-pointer rounded px-1 hover:bg-gray-100"
        onClick={() => setEditing(true)}
        title="Click to edit"
      >
        {value}
      </span>
    );
  }

  return (
    <select
      value={value}
      onChange={(e) => mutation.mutate(e.target.value)}
      onBlur={() => setEditing(false)}
      autoFocus
      disabled={mutation.isPending}
      className="rounded-md border px-2 py-0.5 text-sm"
    >
      {(priorities || []).map((p) => (
        <option key={p.name} value={p.name}>{p.name}</option>
      ))}
    </select>
  );
}

export function EditableAssignee({ issueKey, value, projectKey }: EditableFieldProps) {
  const [editing, setEditing] = useState(false);
  const { data: users } = useAssignableUsers(projectKey);
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: (accountId: string | null) => api.put(`/api/issues/${issueKey}`, { assignee: accountId }),
    onSuccess: () => {
      invalidateIssueViews(queryClient, issueKey);
      setEditing(false);
    },
  });

  if (!editing) {
    return (
      <span
        className="cursor-pointer rounded px-1 hover:bg-gray-100"
        onClick={() => setEditing(true)}
        title="Click to edit"
      >
        {value || 'Unassigned'}
      </span>
    );
  }

  const currentUser = users?.find((u) => u.displayName === value);

  return (
    <select
      value={currentUser?.accountId || ''}
      onChange={(e) => mutation.mutate(e.target.value || null)}
      onBlur={() => setEditing(false)}
      autoFocus
      disabled={mutation.isPending}
      className="rounded-md border px-2 py-0.5 text-sm"
    >
      <AssigneeOptions users={users} />
    </select>
  );
}
